// src/pages/PostContentPage/utils/CreatePost.js

/**
 * @typedef {Object} CreatePostParams
 * @property {string}   userid                          - Required
 * @property {string}   groupid                         - Required (primary group)
 * @property {string}   descriptions                    - Required, min 10 chars
 * @property {string}   location                        - Optional
 * @property {string}   timestamp                       - ISO datetime string
 * @property {string}   eventType                       - Required ("festival"|"concert"|"sports"|...)
 * @property {string|null} eventSegment                 - Optional ("current_month"|"last_month"|"worldwide"|"upcoming")
 * @property {string|null} eventCategory               - Optional ("personal"|"religious"|"social"|"cultural"|"national")
 * @property {boolean}  isPrivate                       - Default false — public post
 * @property {string|null} societyInfo                  - Optional society/organisation name or ID
 * @property {string[]|null} targetGroups               - Optional additional group IDs to share with
 * @property {Array<{ file: File, type: "image"|"video" }>} medias
 */

/**
 * @typedef {Object} CreatePostResponse
 * @property {boolean} success
 * @property {string}  postId
 * @property {string}  message
 */

// ==================== VALIDATION HELPER ====================

/**
 * Validates required and optional CreatePost parameters.
 * Throws descriptive errors on failure.
 *
 * @param {CreatePostParams} params
 */
const validateCreatePostParams = (params) => {
    const { userid, groupid, descriptions, eventType, medias } = params;
  
    // ── Required identifiers ──
    if (!userid  || userid.trim().length  === 0) throw new Error("userid is required");
    if (!groupid || groupid.trim().length === 0) throw new Error("groupid is required");
  
    // ── Event type (mandatory) ──
    if (!eventType || eventType.trim().length === 0)
      throw new Error("Event type is required");
  
    // ── Description ──
    if (!descriptions || descriptions.trim().length === 0)
      throw new Error("Description is required");
    if (descriptions.trim().length < 10)
      throw new Error("Description must be at least 10 characters");
  
    // ── Media ──
    if (!Array.isArray(medias) || medias.length === 0)
      throw new Error("At least one media file is required");
  };
  
  // ==================== CREATE POST (simple upload) ====================
  
  /**
   * Create a post with media files.
   * Handles multipart form upload and all metadata.
   *
   * @param {CreatePostParams} params
   * @returns {Promise<CreatePostResponse>}
   */
  export const CreatePost = async (params) => {
    const {
      userid,
      groupid,
      descriptions,
      location,
      timestamp,
      medias,
      // ── new fields ──
      eventType,
      eventSegment,
      eventCategory,
      isPrivate     = false,
      societyInfo,
      targetGroups,
    } = params;
  
    try {
      // ── Validation ──────────────────────────────────────────────────────
      validateCreatePostParams(params);
  
      // ── Build FormData ───────────────────────────────────────────────────
      const formData = new FormData();
  
      // Core identifiers (profileid removed)
      formData.append("userid",      userid);
      formData.append("groupid",     groupid);
  
      // Event metadata
      formData.append("eventType",   eventType.trim());
      if (eventSegment)  formData.append("eventSegment",  eventSegment);
      if (eventCategory) formData.append("eventCategory", eventCategory);
  
      // Visibility & audience
      formData.append("isPrivate",   String(isPrivate));
      if (societyInfo)               formData.append("societyInfo",  societyInfo.trim());
      if (targetGroups && targetGroups.length > 0)
        formData.append("targetGroups", JSON.stringify(targetGroups));
  
      // Post content
      formData.append("descriptions", descriptions.trim());
      formData.append("location",     location || "");
      formData.append("timestamp",    timestamp);
      formData.append("mediaCount",   String(medias.length));
  
      // Media files
      medias.forEach((media, index) => {
        formData.append(`media_${index}`,     media.file);
        formData.append(`mediaType_${index}`, media.type);
      });
  
      // ── API call ─────────────────────────────────────────────────────────
      const response = await fetch("/api/posts/create", {
        method: "POST",
        body:   formData,
      });
  
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `HTTP Error: ${response.status}`);
      }
  
      const result = await response.json();
      if (!result.success) throw new Error(result.message || "Failed to create post");
  
      console.log("✅ Post created:", result.postId, "| type:", eventType, "| private:", isPrivate);
      return result;
  
    } catch (error) {
      console.error("❌ CreatePost error:", error instanceof Error ? error.message : error);
      throw error;
    }
  };
  
  // ==================== CREATE POST (chunked upload) ====================
  
  /**
   * Upload media in chunks for large files (> 100 MB).
   * Three-step: Initiate → Upload chunks → Finalize.
   * Includes all new metadata fields.
   *
   * @param {CreatePostParams} params
   * @returns {Promise<CreatePostResponse>}
   */
  export const CreatePostWithChunkedUpload = async (params) => {
    const {
      userid,
      groupid,
      descriptions,
      location,
      timestamp,
      medias,
      // ── new fields ──
      eventType,
      eventSegment  = null,
      eventCategory = null,
      isPrivate     = false,
      societyInfo   = null,
      targetGroups  = null,
    } = params;
  
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
  
    try {
      // ── Validation ──────────────────────────────────────────────────────
      validateCreatePostParams(params);
  
      // ── Step 1: Initiate ─────────────────────────────────────────────────
      const initiateResponse = await fetch("/api/posts/initiate", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userid,
          groupid,
          descriptions:  descriptions.trim(),
          location:      location || "",
          timestamp,
          mediaCount:    medias.length,
          // ── new fields ──
          eventType:     eventType.trim(),
          eventSegment:  eventSegment  || null,
          eventCategory: eventCategory || null,
          isPrivate,
          societyInfo:   societyInfo   || null,
          targetGroups:  targetGroups && targetGroups.length > 0 ? targetGroups : null,
        }),
      });
  
      if (!initiateResponse.ok) throw new Error("Failed to initiate post creation");
  
      const { postId, uploadTokens } = await initiateResponse.json();
      console.log("🚀 Chunked upload initiated, postId:", postId);
  
      // ── Step 2: Upload chunks ────────────────────────────────────────────
      for (let i = 0; i < medias.length; i++) {
        const { file }    = medias[i];
        const token       = uploadTokens[i];
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * CHUNK_SIZE;
          const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
  
          const chunkForm = new FormData();
          chunkForm.append("postId",      postId);
          chunkForm.append("mediaIndex",  String(i));
          chunkForm.append("chunkIndex",  String(chunkIndex));
          chunkForm.append("totalChunks", String(totalChunks));
          chunkForm.append("uploadToken", token);
          chunkForm.append("chunk",       chunk);
  
          const chunkResp = await fetch("/api/posts/upload-chunk", {
            method: "POST",
            body:   chunkForm,
          });
  
          if (!chunkResp.ok)
            throw new Error(`Failed to upload chunk ${chunkIndex + 1}/${totalChunks}`);
  
          const pct = (((i + (chunkIndex + 1) / totalChunks) / medias.length) * 100).toFixed(0);
          console.log(`📊 Upload progress: ${pct}%`);
        }
      }
  
      // ── Step 3: Finalize ─────────────────────────────────────────────────
      const finalizeResponse = await fetch("/api/posts/finalize", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId, userid, groupid }),
      });
  
      if (!finalizeResponse.ok) throw new Error("Failed to finalize post creation");
  
      const result = await finalizeResponse.json();
      console.log("✅ Chunked upload complete, postId:", postId, "| type:", eventType, "| private:", isPrivate);
      return result;
  
    } catch (error) {
      console.error("❌ CreatePostWithChunkedUpload error:", error instanceof Error ? error.message : error);
      throw error;
    }
  };
  